import { InfoCircleOutlined } from '@ant-design/icons';
import { Checkbox, Col, Row, Tooltip } from 'antd';
import { Formik } from 'formik';
import { Form, FormItem, Input, InputNumber, ResetButton, Select, SubmitButton } from 'formik-antd';
import capitalize from 'lodash/capitalize';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { DatePicker, IntlMessages, Spinner } from '@/components';
import dayjs from '@/lib/dayjsExtended';
import { imageStatusOptions } from '../../../../const/imageStatuses';
import { imageTypeOptions } from '../../../../const/imageTypes';
import { recommendedTypeOptions } from '../../../../const/recommendedTypes';
import { DATE_FORMAT } from '../../../../const/system';
import getCategoriesOptions from '../../../../lib/getCategoriesOptions';
import getTagsOptions from '../../../../lib/getTagsOptions';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import { useGetCategoriesQuery } from '../../api/categoriesApiSlice';
import { useGetHashtagsQuery } from '../../api/hashtagsApiSlice';
import { useGetTagsQuery } from '../../api/tagsApiSlice';
import { validationSchema } from './FormImagesFilter.const';

const FormImagesFilter = ({
  initialValues,
  formErrors = {},
  onSubmit,
  onCancel,
  isSubmitting,
  excludedFields,
}) => {
  const intl = useIntl();

  const { data: categories = { data: [] }, isLoading: isCategoriesLoading } = useGetCategoriesQuery({
    queryParams: 'limit=0',
  });

  const { data: hashtags = { data: [] }, isLoading: isHashtagsLoading } = useGetHashtagsQuery({
    queryParams: 'limit=0',
  });

  const { data: tags = { data: [] }, isLoading: isTagsLoading } = useGetTagsQuery();

  const categoryOptions = getCategoriesOptions(categories?.data);
  const tagOptions = getTagsOptions(tags?.data);

  if (isCategoriesLoading || isHashtagsLoading || isTagsLoading) {
    return (
      <div className="h-mt-6 text-bold">
        <IntlMessages id="content-image-filter-loading" />
      </div>
    );
  }

  return (
    <Formik
      enableReinitialize
      isSubmitting
      validationSchema={validationSchema}
      initialErrors={formErrors}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values, setFieldValue }) => {
        const isEmptyField = (field) => (isArray(values[field]) && isEmpty(values[field])) || !values[field];

        return (
          <Form className="images-filter w-full" layout="vertical">
            <Spinner spinning={isSubmitting}>
              <Row gutter={16}>
                <Col span={12}>
                  <FormItem
                    name="id"
                    label={
                      <Row className="w-full" justify="space-between" align="middle">
                        <IntlMessages id="ui-general-id" />
                        <Tooltip
                          placement="topRight"
                          title={<IntlMessages id="ui-general-filter-id-key-help" />}
                        >
                          <InfoCircleOutlined className="hp-ml-8" />
                        </Tooltip>
                      </Row>
                    }
                  >
                    <Input
                      name="id"
                      placeholder={intl.formatMessage({ id: 'ui-general-ids-placeholder' })}
                      disabled={!isEmpty(values.folder)}
                    />
                  </FormItem>
                </Col>

                <Col span={12}>
                  <FormItem
                    name="folder"
                    label={
                      <Row className="w-full" justify="space-between" align="middle">
                        <IntlMessages id="ui-general-bee-id" />
                        <Tooltip
                          placement="topRight"
                          title={<IntlMessages id="ui-general-filter-id-key-help" />}
                        >
                          <InfoCircleOutlined className="hp-ml-8" />
                        </Tooltip>
                      </Row>
                    }
                  >
                    <Input
                      name="folder"
                      placeholder={intl.formatMessage({ id: 'content-images-folder-name-placeholder' })}
                      disabled={!isEmpty(values.id)}
                    />
                  </FormItem>
                </Col>

                <Col span={24}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-category-label' })}
                    name="category"
                    className="hp-mb-8"
                  >
                    <Select
                      disabled={values.is_without_category}
                      name="category"
                      mode="multiple"
                      maxTagCount="responsive"
                      allowClear
                      showSearch={{
                        filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                      }}
                      placeholder={intl.formatMessage({ id: 'ui-general-choose-categories' })}
                      options={categoryOptions}
                      loading={isCategoriesLoading}
                      onChange={(value) => {
                        if (isEmpty(value)) {
                          setFieldValue('is_main_category', 0);
                        }

                        setFieldValue('category', value);
                      }}
                    />
                  </FormItem>

                  <Row justify="start">
                    <FormItem className="hp-mr-16 images-filter__item" name="is_without_category">
                      <Checkbox
                        disabled={!isEmptyField('category')}
                        checked={values?.is_without_category ?? false}
                        id="is_without_category"
                        type="checkbox"
                        name="is_without_category"
                        onChange={async (event) => {
                          if (!event.target.checked) {
                            setFieldValue('is_main_category', 0);
                          }
                          await setFieldValue('is_without_category', Number(event.target.checked), true);
                        }}
                      >
                        {intl.formatMessage({ id: 'ui-general-without-category' })}
                      </Checkbox>
                    </FormItem>
                    <FormItem className="images-filter__item" name="is_main_category">
                      <Checkbox
                        disabled={!values.is_without_category && isEmptyField('category')}
                        checked={values?.is_main_category ?? false}
                        id="is_main_category"
                        type="checkbox"
                        name="is_main_category"
                        onChange={async (event) => {
                          await setFieldValue('is_main_category', Number(event.target.checked), true);
                        }}
                      >
                        {intl.formatMessage({ id: 'content-images-only-main-category-label' })}
                      </Checkbox>
                    </FormItem>
                  </Row>
                </Col>

                <Col span={24}>
                  <FormItem label={intl.formatMessage({ id: 'content-images-format-label' })} name="formats">
                    <Select
                      name="formats"
                      mode="multiple"
                      maxTagCount="responsive"
                      allowClear
                      showSearch={{
                        filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                      }}
                      placeholder={intl.formatMessage({ id: 'content-images-format-label-placeholder' })}
                      options={imageTypeOptions}
                    />
                  </FormItem>
                </Col>
                {!excludedFields?.includes('status') && (
                  <Col span={24}>
                    <FormItem label={intl.formatMessage({ id: 'ui-general-status' })} name="status">
                      <Select
                        name="status"
                        mode="multiple"
                        maxTagCount="responsive"
                        allowClear
                        showSearch={{
                          filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                        }}
                        placeholder={intl.formatMessage({ id: 'content-images-status-placeholder' })}
                        options={imageStatusOptions}
                      />
                    </FormItem>
                  </Col>
                )}

                {!excludedFields?.includes('tags') && (
                  <Col span={24}>
                    <FormItem
                      label={intl.formatMessage({ id: 'content-images-tags-label' })}
                      name="tags"
                      className="hp-mb-8"
                    >
                      <Select
                        name="tags"
                        mode="multiple"
                        maxTagCount="responsive"
                        disabled={!isEmptyField('is_without_tags')}
                        allowClear
                        showSearch={{
                          filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                        }}
                        loading={isTagsLoading}
                        placeholder={intl.formatMessage({ id: 'content-images-tags-label-placeholder' })}
                        options={tagOptions}
                      />
                    </FormItem>
                    <Row justify="start">
                      <FormItem className="images-filter__item" name="is_without_tags">
                        <Checkbox
                          className="hp-mr-16"
                          checked={values?.is_without_tags ?? false}
                          disabled={!isEmptyField('tags')}
                          type="checkbox"
                          name="is_without_tags"
                          onChange={async (event) => {
                            if (!event.target.checked) {
                              setFieldValue('is_without_tags', 0);
                            }
                            await setFieldValue('is_without_tags', Number(event.target.checked), true);
                          }}
                        >
                          {intl.formatMessage({ id: 'ui-general-without-tag' })}
                        </Checkbox>
                      </FormItem>
                    </Row>
                  </Col>
                )}

                <Col span={24}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-hashtags-label' })}
                    name="hashtags"
                    className="hp-mb-8"
                  >
                    <Select
                      name="hashtags"
                      mode="multiple"
                      maxTagCount="responsive"
                      allowClear
                      showSearch={{
                        filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                      }}
                      placeholder={intl.formatMessage({ id: 'content-images-hashtags-label-placeholder' })}
                      loading={isHashtagsLoading}
                      options={hashtags.data.map((item) => {
                        return {
                          label: capitalize(item.hashtag),
                          value: item.id,
                        };
                      })}
                    />
                  </FormItem>
                  <Row justify="start">
                    <FormItem className="hp-mr-16 images-filter__item" name="is_exclude_hashtags">
                      <Checkbox
                        disabled={!values.is_exclude_hashtags && isEmptyField('hashtags')}
                        checked={values?.is_exclude_hashtags ?? false}
                        type="checkbox"
                        name="is_exclude_hashtags"
                        onChange={async (event) => {
                          if (!event.target.checked) {
                            setFieldValue('is_exclude_hashtags', 0);
                          }
                          await setFieldValue('is_exclude_hashtags', Number(event.target.checked), true);
                        }}
                      >
                        {intl.formatMessage({ id: 'content-images-exclude-hashtags-label' })}
                      </Checkbox>
                    </FormItem>
                  </Row>
                </Col>

                <Col span={24}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-recommended-types-label' })}
                    name="recommended_types"
                    className="hp-mb-8"
                  >
                    <Select
                      name="recommended_types"
                      mode="multiple"
                      maxTagCount="responsive"
                      allowClear
                      showSearch={{
                        filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                      }}
                      placeholder={intl.formatMessage({ id: 'content-images-recommended-types-placeholder' })}
                      options={recommendedTypeOptions}
                    />
                  </FormItem>
                  <Row justify="start">
                    <FormItem className="hp-mr-16 images-filter__item" name="is_exclude_recommended_types">
                      <Checkbox
                        disabled={!values.is_exclude_recommended_types && isEmptyField('recommended_types')}
                        checked={values?.is_exclude_recommended_types ?? false}
                        type="checkbox"
                        name="is_exclude_recommended_types"
                        onChange={async (event) => {
                          if (!event.target.checked) {
                            setFieldValue('is_exclude_recommended_types', 0);
                          }
                          await setFieldValue(
                            'is_exclude_recommended_types',
                            Number(event.target.checked),
                            true,
                          );
                        }}
                      >
                        {intl.formatMessage({ id: 'content-images-exclude-recommended-types-label' })}
                      </Checkbox>
                    </FormItem>
                  </Row>
                </Col>

                <Col span={6}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-taps-from-label' })}
                    name="taps_from"
                  >
                    <InputNumber
                      min={0}
                      name="taps_from"
                      className="w-full"
                      placeholder={intl.formatMessage({ id: 'ui-general-min' })}
                    />
                  </FormItem>
                </Col>

                <Col span={6}>
                  <FormItem label={intl.formatMessage({ id: 'content-images-taps-to-label' })} name="taps_to">
                    <InputNumber
                      min={0}
                      name="taps_to"
                      className="w-full"
                      placeholder={intl.formatMessage({ id: 'ui-general-max' })}
                    />
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-colors-from-label' })}
                    name="colors_from"
                  >
                    <InputNumber
                      min={0}
                      name="colors_from"
                      className="w-full"
                      placeholder={intl.formatMessage({ id: 'ui-general-min' })}
                    />
                  </FormItem>
                </Col>

                <Col span={6}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-colors-to-label' })}
                    name="colors_to"
                  >
                    <InputNumber
                      min={0}
                      name="colors_to"
                      className="w-full"
                      placeholder={intl.formatMessage({ id: 'ui-general-max' })}
                    />
                  </FormItem>
                </Col>

                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-imported-from-label' })}
                    name="imported_from"
                  >
                    <DatePicker
                      name="imported_from"
                      fromFormikValue={(value) => (value ? dayjs(value, DATE_FORMAT) : null)}
                      toFormikValue={(dateString) => dateString}
                    />
                  </FormItem>
                </Col>

                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'content-images-imported-to-label' })}
                    name="imported_to"
                  >
                    <DatePicker
                      name="imported_to"
                      fromFormikValue={(value) => (value ? dayjs(value, DATE_FORMAT) : null)}
                      toFormikValue={(dateString) => dateString}
                    />
                  </FormItem>
                </Col>

                {!excludedFields?.includes('released_from') && (
                  <Col span={12}>
                    <FormItem
                      label={intl.formatMessage({ id: 'content-images-released-from-label' })}
                      name="released_from"
                    >
                      <DatePicker
                        name="released_from"
                        fromFormikValue={(value) => (value ? dayjs(value, DATE_FORMAT) : null)}
                        toFormikValue={(dateString) => dateString}
                      />
                    </FormItem>
                  </Col>
                )}

                {!excludedFields?.includes('released_to') && (
                  <Col span={12}>
                    <FormItem label={intl.formatMessage({ id: 'ui-general-released-to' })} name="released_to">
                      <DatePicker
                        name="released_to"
                        fromFormikValue={(value) => (value ? dayjs(value, DATE_FORMAT) : null)}
                        toFormikValue={(dateString) => dateString}
                      />
                    </FormItem>
                  </Col>
                )}
              </Row>

              <Row gutter={[16, 16]} justify="end">
                <Col>
                  <ResetButton disabled={false} loading={isSubmitting}>
                    <IntlMessages id="ui-general-reset" />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton loading={isSubmitting} disabled={!isValid || !dirty}>
                    <IntlMessages id="ui-general-apply" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormImagesFilter.propTypes = {
  initialValues: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  excludedFields: PropTypes.array,
};

export default FormImagesFilter;
